"use client";

import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import { useLanguage } from "@/lib/i18n";

const OTAS = [
  { name: "Traveloka", url: "/images/partners/traveloka-logo.webp", height: "h-10 md:h-14", link: "https://www.traveloka.com" },
  { name: "Tiket.com", url: "/images/partners/tiket-com-logo.webp", height: "h-8 md:h-10", link: "https://www.tiket.com" },
  { name: "Agoda", url: "/images/partners/agoda-logo.webp", height: "h-10 md:h-14", link: "https://www.agoda.com" },
  { name: "Booking.com", url: "/images/partners/booking-com-logo.webp", height: "h-8 md:h-10", link: "https://www.booking.com" },
  { name: "Airbnb", url: "/images/partners/aibnb-logo.webp", height: "h-9 md:h-12", link: "https://www.airbnb.com" },
  { name: "Expedia", url: "/images/partners/expedia-logo.webp", height: "h-8 md:h-10", link: "https://www.expedia.com" },
  { name: "Trip.com", url: "/images/partners/trip-com-logo.webp", height: "h-8 md:h-10", link: "https://www.trip.com" },
  { name: "TripAdvisor", url: "/images/partners/tripadvisor-logo.webp", height: "h-10 md:h-14", link: "https://www.tripadvisor.com" },
];

export default function Partners() {
  const { language } = useLanguage();

  const content = language === "id" ? {
    tagline: "Official Partners",
    title: "Pesan dengan Percaya Diri",
    subtitle: "Kami bermitra dengan platform terbaik dunia untuk kenyamanan Anda.",
  } : {
    tagline: "Official Partners",
    title: "Book With Confidence",
    subtitle: "We partner with the world's best platforms for your convenience.",
  };

  return (
    <section className="py-20 bg-[#FAFAFA] border-b border-gray-100 overflow-hidden cursor-grab active:cursor-grabbing">
      <div className="container mx-auto px-0 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16 px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-3">
              {content.tagline}
            </span>
            <h2 className="font-serif text-2xl md:text-3xl text-brand-charcoal mb-3">
              {content.title}
            </h2>
            <p className="text-gray-500 font-light text-sm md:text-base">
              {content.subtitle}
            </p>
          </motion.div>
        </div>

        <div className="relative w-full px-4 md:px-0">
          <div className="absolute top-0 left-0 w-16 md:w-32 h-full bg-gradient-to-r from-[#FAFAFA] to-transparent z-20 pointer-events-none" />
          <div className="absolute top-0 right-0 w-16 md:w-32 h-full bg-gradient-to-l from-[#FAFAFA] to-transparent z-20 pointer-events-none" />

          <Swiper
            modules={[Autoplay]}
            spaceBetween={40}
            slidesPerView={2}
            loop={true}
            speed={2000}
            autoplay={{ delay: 2500, disableOnInteraction: false }}
            breakpoints={{
              640: { slidesPerView: 3, spaceBetween: 50 },
              768: { slidesPerView: 4, spaceBetween: 60 },
              1024: { slidesPerView: 5, spaceBetween: 80 },
            }}
            className="w-full py-4 flex items-center"
          >
            {OTAS.map((ota, idx) => (
              <SwiperSlide key={idx} className="!flex justify-center items-center">
                <LogoItem ota={ota} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}

function LogoItem({ ota }: { ota: { name: string; url: string; height: string; link: string } }) {
  return (
    <a 
      href={ota.link} 
      target="_blank" 
      rel="noopener noreferrer"
      className="relative flex items-center justify-center w-full transition-all duration-500 opacity-100 grayscale-0 md:opacity-50 md:grayscale md:hover:grayscale-0 md:hover:opacity-100"
    >
      <img
        src={ota.url}
        alt={`${ota.name} Logo`}
        className={`${ota.height} w-auto object-contain select-none`}
        draggable="false"
      />
    </a>
  );
}
